import useEmblaCarousel from "embla-carousel-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import s from "./ImageCarousel.module.css";

const ChevronLeft = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M15 18l-6-6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M9 18l6-6-6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function ImageCarousel({ images, productName }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [thumbsRef, thumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const index = emblaApi.selectedScrollSnap();
    setSelectedIndex(index);
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    if (thumbsApi) thumbsApi.scrollTo(index);
  }, [emblaApi, thumbsApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollTo = useCallback(
    (index) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const hasMultiple = images.length > 1;

  return (
    <div
      className={s.carousel}
      role="group"
      aria-roledescription="carousel"
      aria-label={`${productName} images`}
    >
      <div className={s.viewportWrap}>
        <div className={s.viewport} ref={emblaRef}>
          <div className={s.container}>
            {images.map((image, index) => (
              <div
                key={image.product_image_id ?? index}
                className={s.slide}
                role="group"
                aria-roledescription="slide"
                aria-label={`Image ${index + 1} of ${images.length}`}
              >
                <img
                  className={s.slideImage}
                  src={image.image_url}
                  alt={`${productName} - image ${index + 1}`}
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding="async"
                />
              </div>
            ))}
          </div>
        </div>

        {hasMultiple && (
          <>
            <button
              className={`${s.arrow} ${s.arrowPrev}`}
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              aria-label="Previous image"
              type="button"
            >
              <ChevronLeft />
            </button>
            <button
              className={`${s.arrow} ${s.arrowNext}`}
              onClick={scrollNext}
              disabled={!canScrollNext}
              aria-label="Next image"
              type="button"
            >
              <ChevronRight />
            </button>

            <div className={s.counter} aria-live="polite">
              {selectedIndex + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {hasMultiple && (
        <>
          <div className={s.dots} role="tablist" aria-label="Image navigation">
            {images.map((_, index) => (
              <button
                key={index}
                className={`${s.dot} ${
                  index === selectedIndex ? s.dotActive : ""
                }`}
                onClick={() => scrollTo(index)}
                role="tab"
                aria-selected={index === selectedIndex}
                aria-label={`Go to image ${index + 1}`}
                type="button"
              />
            ))}
          </div>

          <div
            className={s.thumbs}
            ref={thumbsRef}
            aria-label="Image thumbnails"
          >
            <div className={s.thumbsContainer}>
              {images.map((image, index) => (
                <button
                  key={image.product_image_id ?? index}
                  className={`${s.thumb} ${
                    index === selectedIndex ? s.thumbActive : ""
                  }`}
                  onClick={() => scrollTo(index)}
                  aria-label={`Go to image ${index + 1}`}
                  aria-current={index === selectedIndex}
                  type="button"
                >
                  <img
                    className={s.thumbImage}
                    src={image.image_url}
                    alt={`${productName} thumbnail ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

ImageCarousel.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      product_image_id: PropTypes.number,
      image_url: PropTypes.string.isRequired,
    }),
  ).isRequired,
  productName: PropTypes.string.isRequired,
};
